<template>
  <div class="flex items-center m-5">
    <font-awesome-icon :icon="['fas', 'users-viewfinder']" class="mr-3 text-3xl"/>
    <p class="text-xl">{{ $t('Mindset360CreatorComponent.furtherInfo') }}</p>
  </div>
  <p class="ml-3 font-medium text-xl" v-html="externalEvaluationIntroText"></p>
  <!----------------- SELECT EXTERNAL EVALUATION SCHEMA ----------------->
  <Dropdown
    v-model="selectedExternalEvaluationSchema"
    :options="externalEvaluationSchemas"
    optionLabel="label"
    :placeholder="$t('Mindset360CreatorComponent.selectExternalEvaluation', { text: featureName })"
    class="w-full mt-3"
  />
  <!----------------- ADD PARTICIPANTS ----------------->
  <MultiSelect v-model="selectedParticipants" display="chip" :options="participants"
        optionLabel="name" filter :placeholder="$t('Mindset360CreatorComponent.addParticipants')"
        class="w-full mt-3" />
  <!-------------- ADD EXTERNAL PARTICIPANTS ----------->
  <external-users-invite-component
    v-if="showExternalEmailsInput"
    class="relative mt-3 w-full"
    :externalUsers="externalUsers"
  />
  <div class="flex sm:my-8 sm:w-fit sm:float-right flex-col sm:flex-row w-full pt-8 items-center
        gap-3">
      <Button id="btn_cancel360" @click.prevent="closePopup"
        class="btn-secondary w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled>
            {{ $t('Mindset360CreatorComponent.cancel') }}
      </Button>
      <Button id="btn_create360" @click.prevent="createMindset360"
        class="btn-primary-accent w-full sm:w-60 min-h-10 py-2 px-4 text-base sm:text-xl" unstyled>
            {{ $t('Mindset360CreatorComponent.sendRequest') }}
      </Button>
    </div>
</template>

<script>
import ExternalUsersInviteComponent from '@/components/Home/KssCreator/ExternalUsersInviteComponent.vue';
import NetworkContent from '@/helpers/networking/NetworkContent';
// import Mindset360ParticipantsSelectionComponent
// from '@/components/Home/Mindset360Creator/Mindset360ParticipantsSelectionComponent.vue';
import { Button, MultiSelect, Dropdown } from 'primevue';
import { mapGetters } from 'vuex';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

export default {
  name: 'Mindset360CreatorComponent',
  props: {
    data: Object,
  },
  components: {
    // Mindset360ParticipantsSelectionComponent,
    ExternalUsersInviteComponent,
    Button,
    MultiSelect,
    Dropdown,
  },
  data() {
    return {
      showParticipants: false,
      selectedParticipants: [],
      selectedExternalEvaluationSchema: null,
      externalEvaluationText: { en: '', de: '' },
    };
  },
  computed: {
    featureName() {
      return getFeatureDisplayNameOrFallback(
        this.$store,
        'externalEvaluation',
        this.$t('FallbackTexts.externalEvaluation'),
        this.$i18n?.locale,
      );
    },
    participants() {
      return this.$store.getters.getExternalEvaluationParticipants;
    },
    externalEvaluationIntroText() {
      const locale = this.$i18n?.locale?.value ?? this.$i18n?.locale ?? 'en';
      return this.externalEvaluationText[locale] || this.externalEvaluationText.en || '';
    },
    ...mapGetters('assessmentSchemas', ['getExternalEvaluationSchemas', 'getLastExternalEvaluationDates']),
    externalEmails() {
      return this.$store.getters.getInviteEmails;
    },
    // Get external evaluation schemas formatted for dropdown (filtered by 6-month cooldown)
    externalEvaluationSchemas() {
      const lastEvalDates = this.getLastExternalEvaluationDates;
      const currentDate = new Date();
      const sixMonthsAgo = new Date(currentDate);
      sixMonthsAgo.setMonth(currentDate.getMonth() - 6);

      // Filter schemas that are available (no recent evaluation or > 6 months ago)
      return this.getExternalEvaluationSchemas
        .filter((schema) => {
          const lastEvalDate = lastEvalDates[schema.type];
          // Schema is available if no previous evaluation
          // OR last evaluation was more than 6 months ago
          if (!lastEvalDate) return true;
          const evalDate = new Date(lastEvalDate);
          return evalDate <= sixMonthsAgo;
        })
        .map((schema) => ({
          label: schema.representationProperties?.assessmentName?.en
            || schema.representationProperties?.assessmentName?.de
            || schema.type
            || 'Unnamed Schema',
          value: schema._id,
          type: schema.type,
          canDisplayExternalEmails: schema.canDisplayExternalEmails || false,
          minParticipants: schema.minParticipants || 7,
          maxParticipants: schema.maxParticipants || 10,
        }));
    },
    // Show external emails input based on selected schema's canDisplayExternalEmails property
    showExternalEmailsInput() {
      return !!(this.selectedExternalEvaluationSchema?.canDisplayExternalEmails);
    },
    /* selectedParticipants() {
      return this.$store.getters.getExternalEvaluationParticipants.filter((el) => el.isChecked);
    }, */
  },
  methods: {
    closePopup() {
      this.$store.commit('removeFrontendOverlay', this.data.id);
      this.$emit('close-overlay');
    },
    async createMindset360() {
      if (this.checkForErrors()) {
        return;
      }
      const data = {
        involvedUsers: this.selectedParticipants
          .map((e) => e._id),
        involvedExternalUsers: this.showExternalEmailsInput
          ? this.$store.state.kss.inviteEmails
          : [],
        externalEvaluationSchemaId: this.selectedExternalEvaluationSchema?.value || null,
        externalEvaluationSchemaType: this.selectedExternalEvaluationSchema?.type || null,
      };
      try {
        await this.$store.dispatch('createExternalEvaluation', data);
        this.$toast.add({
          severity: 'success',
          summary: this.$t('ToastComponent.mindset360.mindset360Created', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
        this.closePopup();
      } catch (error) {
        const roleDeniedHandled = await this.$store.dispatch(
          'assessmentRoleDenied/maybeShowFromApiError',
          error,
          { root: true },
        );
        if (roleDeniedHandled) {
          return;
        }
        this.$toast.add({
          severity: 'error',
          summary: error.response?.data?.message,
          detail: '',
          life: 3000,
        });
      }
    },
    toggleParticipants() {
      this.showParticipants = !this.showParticipants;
    },
    checkForErrors() {
      let hasError = false;

      // Check if an external evaluation schema is selected
      if (!this.selectedExternalEvaluationSchema) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.mindset360.selectExternalEvaluation', { text: this.featureName }),
          detail: '',
          life: 3000,
        });
        hasError = true;
        return hasError;
      }

      // Check for duplicate users in selectedParticipants (by _id)
      const participantIds = this.selectedParticipants.map((p) => p._id);
      const uniqueParticipantIds = new Set(participantIds);
      if (participantIds.length !== uniqueParticipantIds.size) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.mindset360.duplicateParticipants'),
          detail: '',
          life: 3000,
        });
        hasError = true;
        return hasError;
      }

      // Check for duplicate emails in externalEmails
      if (this.showExternalEmailsInput && this.externalEmails.length > 0) {
        const uniqueEmails = new Set(this.externalEmails.map((e) => e.toLowerCase()));
        if (this.externalEmails.length !== uniqueEmails.size) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('ToastComponent.mindset360.duplicateEmails'),
            detail: '',
            life: 3000,
          });
          hasError = true;
          return hasError;
        }

        // Check if any selected participant's email matches an external email
        const participantEmails = this.selectedParticipants
          .map((p) => p.email?.toLowerCase())
          .filter(Boolean);
        const externalEmailsLower = this.externalEmails.map((e) => e.toLowerCase());
        const overlappingEmails = participantEmails.filter(
          (email) => externalEmailsLower.includes(email),
        );
        if (overlappingEmails.length > 0) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('ToastComponent.mindset360.duplicateUserInBothLists'),
            detail: '',
            life: 3000,
          });
          hasError = true;
          return hasError;
        }
      }

      const selectedUserCount = this.selectedParticipants.length
        + (this.showExternalEmailsInput ? this.externalEmails.length : 0);

      // Get min/max participants from selected schema config
      const minParticipants = this.selectedExternalEvaluationSchema?.minParticipants || 7;
      const maxParticipants = this.selectedExternalEvaluationSchema?.maxParticipants || 10;

      if (selectedUserCount < minParticipants || selectedUserCount > maxParticipants) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('ToastComponent.mindset360.mindset360UserCount', {
            min: minParticipants,
            max: maxParticipants,
          }),
          detail: '',
          life: 3000,
        });
        hasError = true;
      }
      return hasError;
    },
  },
  async mounted() {
    this.$store.dispatch('getExternalEvaluationParticipants', this.$store.getters.getUser?.name);
    // Fetch assessment schemas to get external evaluation schemas
    this.$store.dispatch('assessmentSchemas/fetchAssessments');
    // Fetch last external evaluation dates for cooldown filtering
    this.$store.dispatch('assessmentSchemas/fetchLastExternalEvaluationDates');
    // Fetch external evaluation intro text from content management
    this.externalEvaluationText = await NetworkContent.getExternalEvaluationText();
  },
  unmounted() {
    this.$store.commit('resetExternalEvaluationParticipants');
    this.$store.commit('resetInviteEmail');
  },
};
</script>
